import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiTrendingUp, FiClock, FiAward, FiXCircle, FiCheckCircle } from 'react-icons/fi';

const STAGE_CONFIG = {
  total:       { color: '#64748B', bg: '#F1F5F9', label: 'Total',       textColor: '#334155' },
  quotation:   { color: '#6366F1', bg: '#EEF2FF', label: 'Quotation',   textColor: '#3730A3' },
  negotiation: { color: '#3B82F6', bg: '#EFF6FF', label: 'Negotiation', textColor: '#1E40AF' },
  job:         { color: '#10B981', bg: '#ECFDF5', label: 'Job',         textColor: '#065F46' },
  won:         { color: '#10B981', bg: '#ECFDF5', label: 'Job',         textColor: '#065F46' },
  lost:        { color: '#EF4444', bg: '#FEF2F2', label: 'Lost',        textColor: '#991B1B' },
  awaiting:    { color: '#F59E0B', bg: '#FFFBEB', label: 'Awaiting',    textColor: '#92400E' }
};

// Maps API stage name → drill-down funnel_stage param
const STAGE_PARAM = {
  quotation:         'quotation',
  p_number:          'p_number',
  job:               'job',               // signed (released contract) + awaiting contract
  won:               'won',               // signed / released contract only
  lost:              'lost',
  awaiting_contract: 'awaiting_contract'
};

const fmt  = n => Number(n || 0).toLocaleString('en-US');
const fmtW = n => n > 0 ? `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })} MT` : null;
const pct1 = (num, den) => (den > 0 ? Number(((num / den) * 100).toFixed(1)) : 0);

const ConversionFunnelChart = ({ data, loading, error, onDrillDown, subtitle = 'Quotation → Negotiation → Job' }) => {
  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading conversion data</p>
    </Card>
  );

  const all          = data || [];
  const totalItem    = all.find(d => d.stage === 'total');
  const totalCount   = totalItem?.count || 1;
  const quotationIt  = all.find(d => d.stage === 'quotation');
  const negotiationIt= all.find(d => d.stage === 'p_number' || d.type === 'negotiation');
  const signedItem   = all.find(d => d.stage === 'won' || d.type === 'won'); // signed / released contract
  const awaiting     = all.find(d => d.stage === 'awaiting_contract');
  const lostItem     = all.find(d => d.stage === 'lost');

  // The "Job" stage = signed (released contract) + jobs still awaiting the contract.
  const signedCount  = signedItem?.count || 0;
  const awaitCount   = awaiting?.count || 0;
  const jobCount     = signedCount + awaitCount;
  const jobWeight    = (signedItem?.weight || 0) + (awaiting?.weight || 0);
  const negoCount    = negotiationIt?.count || 0;
  const jobItem      = {
    stage: 'job',
    label: 'Job (J#)',
    type:  'job',
    count: jobCount,
    weight: jobWeight,
    rate:  pct1(jobCount, negoCount),
    signedCount,
    awaitCount
  };

  const mainStages = [quotationIt, negotiationIt, jobItem].filter(Boolean);
  const maxCount   = mainStages.reduce((m, d) => Math.max(m, d.count), 1);

  const drill = (item) => {
    if (!onDrillDown || !item || !STAGE_PARAM[item.stage]) return;
    onDrillDown(item.label, { funnel_stage: STAGE_PARAM[item.stage] });
  };

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-primary-50 tw-via-white tw-to-blue-50 tw-shadow-sm">
      <div className="tw-p-5 tw-border-b tw-border-gray-200">
        <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiTrendingUp className="tw-text-primary-600" size={18} />
          Conversion Funnel
        </h3>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
          {subtitle}
          {onDrillDown && <span className="tw-ml-1 tw-text-blue-500">· Click stage to see details</span>}
        </p>
      </div>

      <div className="tw-p-5 tw-space-y-2">
        {/* Total badge */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
          <span className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">Total projects:</span>
          <span className="tw-text-lg tw-font-bold tw-text-primary-700">{fmt(totalCount)}</span>
        </div>

        {/* Main funnel: numbered stages with bars */}
        {mainStages.map((item, i) => {
          const cfg        = STAGE_CONFIG[item.type] || STAGE_CONFIG.quotation;
          const barPct     = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
          const overallPct = totalCount > 0 ? ((item.count / totalCount) * 100).toFixed(1) : 0;
          const isClickable = !!onDrillDown && !!STAGE_PARAM[item.stage];
          const isJob      = item.stage === 'job';

          return (
            <div key={item.stage} className="tw-relative">
              {i < mainStages.length - 1 && (
                <div className="tw-absolute tw-left-3.5 tw-top-full tw-h-2 tw-w-0.5 tw-bg-gray-200" style={{ zIndex: 0 }} />
              )}

              <div
                className={`tw-flex tw-items-center tw-gap-3 tw-relative tw-z-10 tw-rounded-lg tw-p-2.5 tw-transition-all ${isClickable ? 'hover:tw-bg-gray-50 tw-cursor-pointer' : ''}`}
                style={{ backgroundColor: isJob ? cfg.bg : 'transparent' }}
                onClick={() => isClickable && drill(item)}
              >
                <div
                  className="tw-w-7 tw-h-7 tw-rounded-xl tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-text-white tw-text-xs tw-font-bold"
                  style={{ backgroundColor: cfg.color }}
                >
                  {i + 1}
                </div>

                <div className="tw-flex-1">
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                      <span className="tw-text-sm tw-font-semibold" style={{ color: isJob ? cfg.textColor : '#374151' }}>
                        {item.label}
                      </span>
                      {isJob && (
                        <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-text-xs tw-font-bold tw-text-emerald-700 tw-bg-emerald-100 tw-px-1.5 tw-py-0.5 tw-rounded-xl">
                          <FiAward size={10} /> {fmt(item.signedCount)} signed · {fmt(item.awaitCount)} awaiting
                        </span>
                      )}
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span className="tw-text-base tw-font-bold tw-text-gray-900">{fmt(item.count)}</span>
                      <span
                        className="tw-text-xs tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-font-semibold"
                        style={{ backgroundColor: cfg.bg, color: cfg.textColor }}
                      >
                        {overallPct}%
                      </span>
                    </div>
                  </div>
                  <div className="tw-h-2 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden">
                    <div
                      className="tw-h-full tw-rounded-xl tw-transition-all tw-duration-700"
                      style={{ width: `${barPct}%`, backgroundColor: cfg.color }}
                    />
                  </div>
                  <div className="tw-flex tw-items-center tw-justify-between">
                    {item.rate !== null && item.rate !== undefined && (
                      <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">↑ {item.rate}% from previous stage</p>
                    )}
                    {fmtW(item.weight) && (
                      <span className="tw-text-xs tw-text-cyan-600 tw-font-semibold tw-mt-0.5">{fmtW(item.weight)}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Job breakdown (signed / awaiting) + Lost */}
        {(signedItem || awaiting || lostItem) && (
          <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-100 tw-flex tw-flex-wrap tw-gap-3">
            {signedItem && (
              <div
                className={`tw-flex-1 tw-min-w-[150px] tw-flex tw-items-center tw-gap-2 tw-rounded tw-bg-emerald-50 tw-px-3 tw-py-2 ${onDrillDown ? 'tw-cursor-pointer hover:tw-bg-emerald-100 tw-transition-colors' : ''}`}
                onClick={() => onDrillDown && drill(signedItem)}
                title="Jobs whose contract has been released / signed (Contract agreement and beyond)"
              >
                <FiCheckCircle size={14} className="tw-text-emerald-500 tw-flex-shrink-0" />
                <div className="tw-min-w-0">
                  <p className="tw-text-xs tw-text-emerald-700 tw-font-semibold tw-truncate">Signed · contract released</p>
                  <div className="tw-flex tw-items-baseline tw-gap-1.5">
                    <span className="tw-text-base tw-font-bold tw-text-emerald-800">{fmt(signedCount)}</span>
                    <span className="tw-text-xs tw-text-emerald-600">{pct1(signedCount, jobCount)}% of jobs</span>
                  </div>
                  {fmtW(signedItem.weight) && (
                    <p className="tw-text-xs tw-text-cyan-600 tw-font-semibold">{fmtW(signedItem.weight)}</p>
                  )}
                </div>
              </div>
            )}
            {awaiting && (
              <div
                className={`tw-flex-1 tw-min-w-[150px] tw-flex tw-items-center tw-gap-2 tw-rounded tw-bg-amber-50 tw-px-3 tw-py-2 ${onDrillDown ? 'tw-cursor-pointer hover:tw-bg-amber-100 tw-transition-colors' : ''}`}
                onClick={() => onDrillDown && drill(awaiting)}
                title="Projects that already have a job number but have not yet reached the official contract (Contract agreement)"
              >
                <FiClock size={14} className="tw-text-amber-500 tw-flex-shrink-0" />
                <div className="tw-min-w-0">
                  <p className="tw-text-xs tw-text-amber-700 tw-font-semibold tw-truncate">Has J# · awaiting contract</p>
                  <div className="tw-flex tw-items-baseline tw-gap-1.5">
                    <span className="tw-text-base tw-font-bold tw-text-amber-800">{fmt(awaitCount)}</span>
                    <span className="tw-text-xs tw-text-amber-600">{awaiting.rate}% of jobs</span>
                  </div>
                  {fmtW(awaiting.weight) && (
                    <p className="tw-text-xs tw-text-cyan-600 tw-font-semibold">{fmtW(awaiting.weight)}</p>
                  )}
                </div>
              </div>
            )}
            {lostItem && (
              <div
                className={`tw-flex-1 tw-min-w-[150px] tw-flex tw-items-center tw-gap-2 tw-rounded tw-bg-red-50 tw-px-3 tw-py-2 ${onDrillDown ? 'tw-cursor-pointer hover:tw-bg-red-100 tw-transition-colors' : ''}`}
                onClick={() => onDrillDown && drill(lostItem)}
              >
                <FiXCircle size={14} className="tw-text-red-500 tw-flex-shrink-0" />
                <div className="tw-min-w-0">
                  <p className="tw-text-xs tw-text-red-700 tw-font-semibold tw-truncate">Lost</p>
                  <div className="tw-flex tw-items-baseline tw-gap-1.5">
                    <span className="tw-text-base tw-font-bold tw-text-red-800">{fmt(lostItem.count)}</span>
                    <span className="tw-text-xs tw-text-red-600">{lostItem.rate}% of total</span>
                  </div>
                  {fmtW(lostItem.weight) && (
                    <p className="tw-text-xs tw-text-cyan-600 tw-font-semibold">{fmtW(lostItem.weight)}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

ConversionFunnelChart.propTypes = {
  data:        PropTypes.array,
  loading:     PropTypes.bool,
  error:       PropTypes.any,
  onDrillDown: PropTypes.func,
  subtitle:    PropTypes.string
};
ConversionFunnelChart.defaultProps = { data: [], loading: false, error: null, onDrillDown: null };

export default ConversionFunnelChart;
